import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, Clock, Info, Package } from "lucide-react";
import { useTranslation } from 'react-i18next';

export default function DeliveryPointDisplay({ point, onChange, showChangeButton = true }) {
  const { t } = useTranslation();

  return (
    <div className="p-4 border rounded-lg bg-green-50 border-green-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-green-800 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          {t('checkout.delivery.selected_delivery_point')}
        </h4>
        {showChangeButton && (
          <Button 
            variant="outline"
            size="sm"
            onClick={onChange}
            className="text-green-700 border-green-300 hover:bg-green-100"
          >
            {t('checkout.delivery.change_point')}
          </Button>
        )}
      </div>
      
      <div className="space-y-3">
        {/* Delivery Point Name and Service Number */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h5 className="font-medium text-slate-900">
              {point.name || point.properties?.clusterCaption}
            </h5>
            {point.serviceNumber && (
              <p className="text-sm text-slate-600">
                {t('checkout.delivery_point.code')} {point.serviceNumber}
              </p>
            )}
          </div>
          <Badge variant="secondary" className="ml-2">
            {point.type === 'pickup' ? t('checkout.delivery_point.pickup_point') : t('checkout.delivery_point.store')}
          </Badge>
        </div>

        {/* Address */}
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-700">
            {point.city && (
              <div>{point.city}</div>
            )}
            {point.street && (
              <div>
                {point.street}
                {point.house && `, д. ${point.house}`}
                {point.housing && `, корп. ${point.housing}`}
              </div>
            )}
            {point.postCode && (
              <div className="text-slate-500">{t('checkout.delivery.postal_code_label')} {point.postCode}</div>
            )}
          </div>
        </div>

        {/* Working Hours */}
        {point.workTimeRaw && 
         point.workTimeRaw !== 'NaN:NaN - NaN:NaN;NaN:NaN - NaN:NaN;NaN:NaN - NaN:NaN;NaN:NaN - NaN:NaN;NaN:NaN - NaN:NaN;;' && (
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-slate-700">
              <div className="font-medium">{t('checkout.delivery_point.working_hours')}</div>
              <div>{point.workTimeRaw}</div>
            </div>
          </div>
        )}

        {/* Phone */}
        {point.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-500" />
            <a 
              href={`tel:${point.phone}`}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {point.phone}
            </a>
          </div>
        )}

        {/* Comment */}
        {point.comment && (
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-slate-700">
              <div className="font-medium">{t('checkout.delivery_point.additional_info')}</div>
              <div>{point.comment}</div>
            </div>
          </div>
        )}

        {/* Weight Limit */}
        {point.weightMax && (
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-500" />
            <div className="text-sm text-slate-700">
              {t('checkout.delivery_point.max_weight')} {point.weightMax} кг
            </div>
          </div>
        )}

        {/* Shelf Life */}
        {point.shelfLifeDays && (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <div className="text-sm text-slate-700">
              {t('checkout.delivery_point.shelf_life')} {point.shelfLifeDays} {t('checkout.delivery_point.days')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
